import { useTranslation } from 'react-i18next';
import { Crown, Shield, TrendingUp, AlertTriangle, ArrowUp } from 'lucide-react';

import type { CoachAnalysis as CoachAnalysisData, AiUsage } from '@/types/qbot';

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  body: string;
}

function Section({ icon, title, body }: SectionProps) {
  return (
    <section className="rounded-xl bg-bg-secondary p-3">
      <h2 className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand">
        <span className="text-brand">{icon}</span>
        {title}
      </h2>
      <p className="whitespace-pre-line text-sm leading-relaxed text-fg">{body}</p>
    </section>
  );
}

/**
 * Shared render of Ramon's deck analysis — the Win condition / Strengths /
 * Weaknesses / Watch out / Upgrade priority sections plus the usage line.
 *
 * Extracted from the (now removed) standalone CRCoach screen so the analysis
 * can be embedded anywhere a deck gets analysed: saved/popular decks, the
 * deck-check screen, etc. All copy lives in the `cr` namespace under
 * `coach.section.*` + `coach.usage`.
 */
export function CoachAnalysis({
  analysis,
  usage,
}: {
  analysis: CoachAnalysisData;
  usage?: AiUsage;
}) {
  const { t } = useTranslation('cr');

  return (
    <div className="flex flex-col gap-3">
      <Section
        icon={<Crown className="h-4 w-4" />}
        title={t('coach.section.winCondition')}
        body={analysis.winCondition}
      />
      <Section
        icon={<TrendingUp className="h-4 w-4" />}
        title={t('coach.section.strengths')}
        body={analysis.strengths}
      />
      <Section
        icon={<Shield className="h-4 w-4" />}
        title={t('coach.section.weaknesses')}
        body={analysis.weaknesses}
      />
      <Section
        icon={<AlertTriangle className="h-4 w-4" />}
        title={t('coach.section.watchOutFor')}
        body={analysis.watchOutFor}
      />
      <Section
        icon={<ArrowUp className="h-4 w-4" />}
        title={t('coach.section.upgradePriority')}
        body={analysis.upgradePriority}
      />
      {usage && (
        <p className="text-[10px] text-fg-muted">
          {t('coach.usage', {
            model: usage.model,
            cost: usage.costUsd.toFixed(4),
            ms: usage.latencyMs,
          })}
        </p>
      )}
    </div>
  );
}
